import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api/client";
import SellerOrdersPanel from "./SellerOrdersPanel";
import { trackDashboardUsage, trackFilterUse } from "../utils/analytics";

const initialForm = {
  name: "",
  description: "",
  price: "",
  stockQty: "1",
  category: "accessory",
  productType: "accessory",
  shippingScope: "india",
  shippingIndia: "120",
  shippingTamilNadu: "250",
  tags: "",
  image: null,
  videoUrl: "",
  salesChannel: "retail",
  wholesalePrice: "",
  wholesaleMinQty: "0",
  wholesaleNotes: "",
};

const initialPromotionForm = {
  title: "",
  code: "",
  type: "first-order",
  audience: "retail",
  discountType: "percent",
  discountValue: "10",
  maxDiscount: "300",
  minOrderValue: "0",
  appliesToProductType: "all",
  appliesToCategories: "",
  firstOrderOnly: true,
  freeShipping: false,
  freeGiftItemId: "",
  freeGiftLabel: "",
  startDate: "",
  endDate: "",
  isActive: true,
};

const inferTradeAccess = (seller = {}) => {
  const businessType = seller.businessType || "retail";
  return {
    canBuyWholesale:
      seller.canBuyWholesale !== undefined
        ? Boolean(seller.canBuyWholesale)
        : ["retail", "hybrid"].includes(businessType),
    canSellRetail:
      seller.canSellRetail !== undefined
        ? Boolean(seller.canSellRetail)
        : ["retail", "hybrid"].includes(businessType),
    canSellWholesale:
      seller.canSellWholesale !== undefined
        ? Boolean(seller.canSellWholesale)
        : ["wholesale", "hybrid"].includes(businessType),
  };
};

const getListingCheckpoint = (item, access) => {
  const listingStatus = item.listingStatus || "active";
  const channel = item.salesChannel || "retail";
  if (listingStatus !== "active") {
    return {
      label: listingStatus === "review" ? "Waiting for admin review" : "Hidden from storefront",
      note: `Listing status is ${listingStatus}.`,
    };
  }
  if ((channel === "retail" || channel === "both") && !access.canSellRetail) {
    return {
      label: "Retail disabled for account",
      note: "Super admin needs to enable direct retail selling.",
    };
  }
  if ((channel === "wholesale" || channel === "both") && !access.canSellWholesale) {
    return {
      label: "Wholesale disabled for account",
      note: "Super admin needs to enable wholesale supply.",
    };
  }
  if (Number(item.stockQty || 0) <= 0) {
    return {
      label: "Out of stock",
      note: "Customers cannot buy until stock is increased.",
    };
  }
  if (channel === "wholesale") {
    return {
      label: "Private wholesale only",
      note: "Visible only to approved trade buyers.",
    };
  }
  return {
    label: "Visible on storefront",
    note: channel === "both" ? "Shown to retail customers and trade buyers." : "Shown to retail customers.",
  };
};

const SellerDashboard = () => {
  const location = useLocation();
  const storedSeller = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("seller") || "null");
    } catch {
      return null;
    }
  }, []);

  const [seller, setSeller] = useState(storedSeller);
  const [items, setItems] = useState([]);
  const [tradeItems, setTradeItems] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState("");
  const [promotions, setPromotions] = useState([]);
  const [promotionForm, setPromotionForm] = useState(initialPromotionForm);
  const [editingPromotionId, setEditingPromotionId] = useState("");
  const [listingFilter, setListingFilter] = useState("all");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const loadProducts = async () => {
    try {
      const response = await api.get("/api/items/mine/list", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      });
      setItems(response.data || []);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to load products");
    }
  };

  const loadSellerProfile = async () => {
    try {
      const response = await api.get("/api/sellers/me", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      });
      if (response.data?.seller) {
        setSeller(response.data.seller);
        localStorage.setItem("seller", JSON.stringify(response.data.seller));
      }
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to refresh seller access");
    }
  };

  const loadPromotions = async () => {
    try {
      const response = await api.get("/api/promotions/mine", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      });
      setPromotions(response.data || []);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to load promotions");
    }
  };

  const loadTradeCatalog = async () => {
    const access = inferTradeAccess(seller);
    if (!access.canBuyWholesale) return;

    try {
      const response = await api.get("/api/items/trade/catalog", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      });
      setTradeItems(response.data?.items || []);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to load wholesale catalog");
    }
  };

  useEffect(() => {
    loadSellerProfile();
    loadProducts();
    loadPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTradeCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller?._id, seller?.canBuyWholesale]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, [location.hash]);

  useEffect(() => {
    const section = location.hash ? location.hash.replace("#", "") : "seller-overview";
    trackDashboardUsage({
      role: "seller",
      section,
      path: `${location.pathname}${location.hash || ""}`,
    });
  }, [location.hash, location.pathname]);

  const access = inferTradeAccess(seller || {});
  const canSellRetail = access.canSellRetail;
  const canSellWholesale = access.canSellWholesale;
  const activeSellerId = String(seller?._id || seller?.id || "");
  const availableSalesChannels = [
    canSellRetail ? { value: "retail", label: "Retail only" } : null,
    canSellWholesale ? { value: "wholesale", label: "Wholesale only" } : null,
    canSellRetail && canSellWholesale ? { value: "both", label: "Retail + Wholesale" } : null,
  ].filter(Boolean);

  const normalizeAllowedSalesChannel = (value) => {
    if (availableSalesChannels.some((channel) => channel.value === value)) return value;
    return availableSalesChannels[0]?.value || "retail";
  };

  const ownedItems = useMemo(() => (
    items.filter((item) => {
      const itemSellerId = String(item?.sellerId?._id || item?.sellerId || "");
      if (!activeSellerId) return true;
      return itemSellerId === activeSellerId;
    })
  ), [activeSellerId, items]);

  const listingStats = useMemo(() => {
    const rows = ownedItems.map((item) => ({ item, checkpoint: getListingCheckpoint(item, access) }));
    return {
      total: ownedItems.length,
      public: rows.filter((row) => row.checkpoint.label === "Visible on storefront").length,
      soldOut: rows.filter((row) => Number(row.item.stockQty || 0) <= 0 || row.item.sold === true).length,
      review: rows.filter((row) => (row.item.listingStatus || "active") === "review").length,
      hidden: rows.filter((row) => (row.item.listingStatus || "active") === "hidden").length,
      wholesale: rows.filter((row) => row.checkpoint.label === "Private wholesale only").length,
    };
  }, [access, ownedItems]);

  const offerStats = useMemo(() => ({
    total: promotions.length,
    active: promotions.filter((promotion) => promotion.isActive).length,
    gifts: promotions.filter((promotion) => promotion.type === "free-gift").length,
    coupons: promotions.filter((promotion) => promotion.type === "coupon").length,
  }), [promotions]);

  const filteredSellerItems = useMemo(() => {
    return ownedItems.filter((item) => {
      const checkpoint = getListingCheckpoint(item, access);
      if (listingFilter === "all") return true;
      if (listingFilter === "public") return checkpoint.label === "Visible on storefront";
      if (listingFilter === "sold-out") return Number(item.stockQty || 0) <= 0 || item.sold === true;
      if (listingFilter === "review") return (item.listingStatus || "active") === "review";
      if (listingFilter === "hidden") return (item.listingStatus || "active") === "hidden";
      if (listingFilter === "wholesale") return checkpoint.label === "Private wholesale only";
      return true;
    });
  }, [access, listingFilter, ownedItems]);

  const jumpToListings = (filter) => {
    setListingFilter(filter);
    trackFilterUse({
      filterType: "seller_listing_status",
      filterValue: filter,
      context: "seller_dashboard",
    });
    document.getElementById("seller-listings")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    const formData = new FormData();
    const shippingRates =
      form.productType === "live-fish"
        ? { TN: Number(form.shippingTamilNadu || 250) }
        : { INDIA: Number(form.shippingIndia || 120) };

    Object.entries({
      name: form.name,
      description: form.description,
      price: form.price,
      stockQty: form.stockQty,
      category: form.productType === "live-fish" ? "livestock" : form.category,
      productType: form.productType,
      shippingScope: form.productType === "live-fish" ? "tamil-nadu" : "india",
      shippingRates: JSON.stringify(shippingRates),
      tags: form.tags,
      videoUrl: form.videoUrl,
      salesChannel: normalizeAllowedSalesChannel(form.salesChannel),
      wholesalePrice: form.wholesalePrice || 0,
      wholesaleMinQty: form.wholesaleMinQty || 0,
      wholesaleNotes: form.wholesaleNotes,
    }).forEach(([key, value]) => formData.append(key, value));

    if (form.image) {
      formData.append("image", form.image);
    }

    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        "Content-Type": "multipart/form-data",
      };

      if (editingId) {
        await api.put(`/api/items/${editingId}`, formData, { headers });
        setMessage("Product updated.");
      } else {
        await api.post("/api/items", formData, { headers });
        setMessage("Product created.");
      }

      setForm(initialForm);
      setEditingId("");
      await loadProducts();
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price || "",
      stockQty: item.stockQty || "0",
      category: item.category || "accessory",
      productType: item.productType || "accessory",
      shippingScope: item.shippingScope || "india",
      shippingIndia: item.shippingRates?.INDIA ?? item.shippingRates?.OTHER ?? "120",
      shippingTamilNadu: item.shippingRates?.TN ?? "250",
      tags: Array.isArray(item.tags) ? item.tags.join(", ") : "",
      image: null,
      videoUrl: item.videoUrl || "",
      salesChannel: item.salesChannel || "retail",
      wholesalePrice: item.wholesalePrice || "",
      wholesaleMinQty: item.wholesaleMinQty || "0",
      wholesaleNotes: item.wholesaleNotes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/api/items/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      });
      setMessage("Product removed.");
      await loadProducts();
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to delete product");
    }
  };

  const submitPromotion = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const payload = {
        ...promotionForm,
        code: promotionForm.code.trim().toUpperCase(),
        discountValue: Number(promotionForm.discountValue || 0),
        maxDiscount: Number(promotionForm.maxDiscount || 0),
        minOrderValue: Number(promotionForm.minOrderValue || 0),
        appliesToCategories: promotionForm.appliesToCategories,
      };

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      };
      if (editingPromotionId) {
        await api.put(`/api/promotions/${editingPromotionId}`, payload, config);
      } else {
        await api.post("/api/promotions", payload, config);
      }
      setPromotionForm(initialPromotionForm);
      setEditingPromotionId("");
      setMessage(editingPromotionId ? "Promotion updated." : "Promotion created.");
      await loadPromotions();
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to create promotion");
    } finally {
      setLoading(false);
    }
  };

  const startEditPromotion = (promotion) => {
    setEditingPromotionId(promotion._id);
    setPromotionForm({
      title: promotion.title || "",
      code: promotion.code || "",
      type: promotion.type || "first-order",
      audience: promotion.audience || "retail",
      discountType: promotion.discountType || "percent",
      discountValue: promotion.discountValue || "0",
      maxDiscount: promotion.maxDiscount || "0",
      minOrderValue: promotion.minOrderValue || "0",
      appliesToProductType: promotion.appliesToProductType || "all",
      appliesToCategories: Array.isArray(promotion.appliesToCategories)
        ? promotion.appliesToCategories.join(", ")
        : "",
      firstOrderOnly: Boolean(promotion.firstOrderOnly),
      freeShipping: Boolean(promotion.freeShipping),
      freeGiftItemId: promotion.freeGiftItemId?._id || promotion.freeGiftItemId || "",
      freeGiftLabel: promotion.freeGiftLabel || "",
      startDate: promotion.startDate ? String(promotion.startDate).slice(0, 10) : "",
      endDate: promotion.endDate ? String(promotion.endDate).slice(0, 10) : "",
      isActive: promotion.isActive !== false,
    });
    document.getElementById("seller-offers")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const deletePromotion = async (promotion) => {
    if (!window.confirm(`Delete offer "${promotion.title}"?`)) return;
    try {
      await api.delete(`/api/promotions/${promotion._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
        },
      });
      setMessage("Promotion deleted.");
      await loadPromotions();
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to delete promotion");
    }
  };

  const togglePromotion = async (promotion) => {
    try {
      await api.put(
        `/api/promotions/${promotion._id}`,
        { isActive: !promotion.isActive },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("seller-token") || ""}`,
          },
        }
      );
      await loadPromotions();
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to update promotion");
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="market-shell">
      <section className="dashboard-panel" id="seller-products">
        <div className="dashboard-heading">
          <div>
            <h2>{seller?.storeName || "Seller Dashboard"}</h2>
            <p>
              {access.canBuyWholesale
                ? "Buy wholesale stock privately and manage your approved selling channels."
                : "Manage products for the channels enabled by super admin."}
            </p>
          </div>
          <div className="dashboard-pill">Products: {ownedItems.length}</div>
        </div>

        <div className="info-banner">
          Logged in as: <strong>{seller?.storeName || seller?.name || seller?.email || "Seller"}</strong>
          {seller?.email ? ` (${seller.email})` : ""} •{" "}
          Access: {access.canBuyWholesale ? "Can buy wholesale" : "No wholesale buying"} •{" "}
          {canSellRetail ? "Can sell to customers" : "Retail selling disabled"} •{" "}
          {canSellWholesale ? "Can supply wholesale" : "Wholesale supply disabled"}
        </div>

        <div style={quickNav}>
          <button type="button" onClick={() => scrollToSection("seller-products")}>
            Add / Edit Product
          </button>
          <button type="button" onClick={() => scrollToSection("seller-orders")}>
            Manage Orders
          </button>
          <button type="button" onClick={() => scrollToSection("seller-offers")}>
            Add Offer
          </button>
          <button type="button" onClick={() => scrollToSection("seller-listings")}>
            View Listings
          </button>
        </div>

        <div style={sellerMetricGrid}>
          {[
            ["All listings", listingStats.total, "all"],
            ["Public", listingStats.public, "public"],
            ["Sold out", listingStats.soldOut, "sold-out"],
            ["Needs review", listingStats.review, "review"],
            ["Hidden", listingStats.hidden, "hidden"],
            ["Private wholesale", listingStats.wholesale, "wholesale"],
          ].map(([label, value, filter]) => (
            <button key={label} type="button" style={sellerMetricCard} onClick={() => jumpToListings(filter)}>
              <span>{label}</span>
              <strong>{value}</strong>
            </button>
          ))}
        </div>

        <form className="seller-form-grid" onSubmit={submitProduct}>
          {!availableSalesChannels.length ? (
            <div className="info-banner" style={{ gridColumn: "1 / -1" }}>
              Product selling is disabled for this account. Please contact super admin to enable retail selling or wholesale supply.
            </div>
          ) : null}
          <input
            placeholder="Product name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            required
          />
          <select
            value={form.productType}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                productType: event.target.value,
                category: event.target.value === "live-fish" ? "livestock" : "accessory",
              }))
            }
          >
            <option value="accessory">Accessory</option>
            <option value="live-fish">Live Fish</option>
          </select>
          <input
            type="number"
            min="1"
            placeholder="Price"
            value={form.price}
            onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))}
            required
          />
          <input
            type="number"
            min="0"
            placeholder="Stock"
            value={form.stockQty}
            onChange={(event) => setForm((prev) => ({ ...prev, stockQty: event.target.value }))}
            required
          />
          <textarea
            placeholder="Description"
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            required
          />
          <input
            placeholder="Tags"
            value={form.tags}
            onChange={(event) => setForm((prev) => ({ ...prev, tags: event.target.value }))}
          />
          {form.productType === "live-fish" ? (
            <input
              type="number"
              min="0"
              placeholder="Tamil Nadu shipping"
              value={form.shippingTamilNadu}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, shippingTamilNadu: event.target.value }))
              }
            />
          ) : (
            <input
              type="number"
              min="0"
              placeholder="All India shipping"
              value={form.shippingIndia}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, shippingIndia: event.target.value }))
              }
            />
          )}
          <input
            type="url"
            placeholder="Video URL"
            value={form.videoUrl}
            onChange={(event) => setForm((prev) => ({ ...prev, videoUrl: event.target.value }))}
          />
          <select
            value={normalizeAllowedSalesChannel(form.salesChannel)}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, salesChannel: normalizeAllowedSalesChannel(event.target.value) }))
            }
          >
            {availableSalesChannels.map((channel) => (
              <option key={channel.value} value={channel.value}>
                {channel.label}
              </option>
            ))}
          </select>
          {canSellWholesale ? (
            <>
              <input
                type="number"
                min="0"
                placeholder="Wholesale price"
                value={form.wholesalePrice}
                onChange={(event) => setForm((prev) => ({ ...prev, wholesalePrice: event.target.value }))}
              />
              <input
                type="number"
                min="0"
                placeholder="Minimum wholesale quantity"
                value={form.wholesaleMinQty}
                onChange={(event) => setForm((prev) => ({ ...prev, wholesaleMinQty: event.target.value }))}
              />
              <textarea
                placeholder="Wholesale notes for shopkeepers"
                value={form.wholesaleNotes}
                onChange={(event) => setForm((prev) => ({ ...prev, wholesaleNotes: event.target.value }))}
              />
            </>
          ) : null}
          <input
            type="file"
            accept="image/*"
            onChange={(event) =>
              setForm((prev) => ({ ...prev, image: event.target.files?.[0] || null }))
            }
          />
          <button type="submit" disabled={loading || !availableSalesChannels.length}>
            {loading ? "Saving..." : editingId ? "Update Product" : "Add Product"}
          </button>
          {editingId ? (
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setEditingId("");
                setForm(initialForm);
              }}
            >
              Cancel Edit
            </button>
          ) : null}
        </form>

        {message ? <div className="info-banner">{message}</div> : null}
      </section>

      {access.canBuyWholesale ? (
        <section className="dashboard-panel">
          <div className="dashboard-heading">
            <div>
              <h3>Private Wholesale Catalog</h3>
              <p>Approved retailer/shopkeeper access only. These listings are hidden from public customers.</p>
            </div>
            <div className="dashboard-pill">Wholesale: {tradeItems.length}</div>
          </div>

          <div className="seller-product-list">
            {tradeItems.map((item) => {
              const sellerName = item.sellerName || item.sellerId?.storeName || "Wholesaler";
              const sellerPhone = item.sellerId?.phone || "";
              const target = sellerPhone.replace(/\D/g, "") || "918072968764";
              const message = [
                `Hello, I want to buy wholesale stock from Vanigam360.`,
                `Product: ${item.name}`,
                `MOQ: ${Number(item.wholesaleMinQty || 1)}`,
                `Wholesale price: Rs.${Number(item.wholesalePrice || item.price || 0)}`,
                `Supplier: ${sellerName}`,
              ].join("\n");

              return (
                <article key={item._id} className="seller-product-card">
                  <div>
                    <strong>{item.name}</strong>
                    <p>{sellerName} • Stock {item.stockQty}</p>
                    <p>
                      Bulk Rs.{Number(item.wholesalePrice || item.price || 0).toFixed(0)} • MOQ{" "}
                      {Number(item.wholesaleMinQty || 1)}
                    </p>
                    <p>{item.wholesaleNotes || "Wholesale supply available for approved retailers."}</p>
                  </div>
                  <div className="seller-actions">
                    <a
                      href={`https://wa.me/${target}?text=${encodeURIComponent(message)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Enquire
                    </a>
                  </div>
                </article>
              );
            })}
            {tradeItems.length === 0 ? <p>No wholesale products available for your account yet.</p> : null}
          </div>
        </section>
      ) : null}

      <SellerOrdersPanel seller={seller} />

      <section className="dashboard-panel" id="seller-offers">
        <div className="dashboard-heading">
          <div>
            <h3>Offers and Promotions</h3>
            <p>Create first-order discounts, coupon codes, free shipping, or free gifts.</p>
          </div>
          <div className="dashboard-pill">Active: {offerStats.active} / {offerStats.total}</div>
        </div>

        <div style={sellerMetricGrid}>
          {[
            ["Total offers", offerStats.total],
            ["Active offers", offerStats.active],
            ["Free gifts", offerStats.gifts],
            ["Coupons", offerStats.coupons],
          ].map(([label, value]) => (
            <div key={label} style={sellerMetricCard}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>

        <div className="seller-insight-card" style={{ marginBottom: 16 }}>
          <strong>How to use offers</strong>
          <p>
            First-order discounts apply automatically for a customer's first paid order. Coupon codes need the
            customer to enter the code at checkout. Free shipping and free gifts apply automatically when the cart
            reaches your minimum order and product rules. Keep gift stock available, set an end date for campaigns,
            and disable offers when the campaign is finished.
          </p>
        </div>

        <form className="seller-form-grid" onSubmit={submitPromotion}>
          <input
            placeholder="Offer title"
            value={promotionForm.title}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, title: event.target.value }))}
            required
          />
          <select
            value={promotionForm.type}
            onChange={(event) =>
              setPromotionForm((prev) => ({
                ...prev,
                type: event.target.value,
                firstOrderOnly: event.target.value === "first-order",
              }))
            }
          >
            <option value="first-order">New customer first purchase discount</option>
            <option value="coupon">Coupon Code</option>
            <option value="free-shipping">Free Shipping</option>
            <option value="free-gift">Free Gift</option>
          </select>
          <select
            value={promotionForm.audience}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, audience: event.target.value }))}
          >
            <option value="retail">Retail customers</option>
            <option value="wholesale">Wholesale buyers</option>
            <option value="all">Both retail and wholesale</option>
          </select>
          <input
            placeholder="Coupon code"
            value={promotionForm.code}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, code: event.target.value }))}
          />
          <select
            value={promotionForm.discountType}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, discountType: event.target.value }))}
            disabled={["free-shipping", "free-gift"].includes(promotionForm.type)}
          >
            <option value="percent">Percent</option>
            <option value="flat">Flat</option>
          </select>
          <input
            type="number"
            min="0"
            placeholder="Discount value"
            value={promotionForm.discountValue}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, discountValue: event.target.value }))}
            disabled={["free-shipping", "free-gift"].includes(promotionForm.type)}
          />
          <input
            type="number"
            min="0"
            placeholder="Max discount"
            value={promotionForm.maxDiscount}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, maxDiscount: event.target.value }))}
            disabled={["free-shipping", "free-gift"].includes(promotionForm.type)}
          />
          <input
            type="number"
            min="0"
            placeholder="Minimum order"
            value={promotionForm.minOrderValue}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, minOrderValue: event.target.value }))}
          />
          <select
            value={promotionForm.appliesToProductType}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, appliesToProductType: event.target.value }))}
          >
            <option value="all">All Products</option>
            <option value="live-fish">Live Fish Only</option>
            <option value="accessory">Accessories Only</option>
          </select>
          <input
            placeholder="Categories (optional, comma separated)"
            value={promotionForm.appliesToCategories}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, appliesToCategories: event.target.value }))}
          />
          <select
            value={promotionForm.freeGiftItemId}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, freeGiftItemId: event.target.value }))}
            disabled={promotionForm.type !== "free-gift"}
          >
            <option value="">Select Free Gift Product</option>
            {items.map((item) => (
              <option key={item._id} value={item._id}>
                {item.name}
              </option>
            ))}
          </select>
          <input
            placeholder="Free gift label (optional)"
            value={promotionForm.freeGiftLabel}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, freeGiftLabel: event.target.value }))}
            disabled={promotionForm.type !== "free-gift"}
          />
          <input
            type="date"
            value={promotionForm.startDate}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, startDate: event.target.value }))}
            title="Offer start date"
          />
          <input
            type="date"
            value={promotionForm.endDate}
            onChange={(event) => setPromotionForm((prev) => ({ ...prev, endDate: event.target.value }))}
            title="Offer end date"
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={promotionForm.firstOrderOnly}
              disabled={promotionForm.type === "first-order"}
              onChange={(event) => setPromotionForm((prev) => ({ ...prev, firstOrderOnly: event.target.checked }))}
            />
            First purchase only
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={promotionForm.isActive}
              onChange={(event) => setPromotionForm((prev) => ({ ...prev, isActive: event.target.checked }))}
            />
            Active offer
          </label>
          <button type="submit" disabled={loading}>
            {loading ? "Saving..." : editingPromotionId ? "Update Offer" : "Create Offer"}
          </button>
          {editingPromotionId ? (
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                setEditingPromotionId("");
                setPromotionForm(initialPromotionForm);
              }}
            >
              Cancel Offer Edit
            </button>
          ) : null}
        </form>

        <div className="seller-product-list" style={{ marginTop: 20 }}>
          {promotions.map((promotion) => (
            <article key={promotion._id} className="seller-product-card">
              <div>
                <strong>{promotion.title}</strong>
                <p>
                  {promotion.type} {promotion.code ? `• ${promotion.code}` : ""} • {promotion.audience || "retail"}
                </p>
                <p>
                  Min Rs.{promotion.minOrderValue || 0}
                  {promotion.freeGiftLabel ? ` • Gift: ${promotion.freeGiftLabel}` : ""}
                  {promotion.usageCount ? ` • Used ${promotion.usageCount} times` : ""}
                </p>
              </div>
              <div className="seller-actions">
                <button type="button" onClick={() => startEditPromotion(promotion)}>
                  Edit
                </button>
                <button type="button" onClick={() => togglePromotion(promotion)}>
                  {promotion.isActive ? "Disable" : "Enable"}
                </button>
                <button type="button" className="secondary-button" onClick={() => deletePromotion(promotion)}>
                  Delete
                </button>
              </div>
            </article>
          ))}
          {promotions.length === 0 ? <p>No promotions created yet.</p> : null}
        </div>
      </section>

      <section className="dashboard-panel" id="seller-listings">
        <div className="dashboard-heading">
          <div>
            <h3>Your Listings</h3>
            <p>Everything in one place for fast inventory control.</p>
          </div>
          <select value={listingFilter} onChange={(event) => setListingFilter(event.target.value)}>
            <option value="all">All listings</option>
            <option value="public">Public</option>
            <option value="sold-out">Sold out</option>
            <option value="review">Needs review</option>
            <option value="hidden">Hidden</option>
            <option value="wholesale">Private wholesale</option>
          </select>
        </div>

        <div className="seller-product-list">
          {filteredSellerItems.map((item) => {
            const checkpoint = getListingCheckpoint(item, access);
            const soldOut = Number(item.stockQty || 0) <= 0 || item.sold === true;
            return (
              <article key={item._id} className="seller-product-card">
                <div>
                  <strong>{item.name}</strong>
                  <p>
                    {item.productType === "live-fish" ? "Live Fish" : "Accessory"} • Stock {item.stockQty}
                  </p>
                  <p>
                    ₹{item.price}
                    {item.salesChannel === "wholesale" ? " • Wholesale only" : ""}
                    {item.salesChannel === "both" ? " • Retail + Wholesale" : ""}
                  </p>
                  {Number(item.wholesalePrice || 0) > 0 ? (
                    <p>
                      Bulk Rs.{Number(item.wholesalePrice).toFixed(0)} • MOQ {Number(item.wholesaleMinQty || 0)}
                    </p>
                  ) : null}
                  <p style={{ fontWeight: 700, color: soldOut ? "#b91c1c" : "#0f766e" }}>
                    {soldOut ? "Sold Out / Restock needed" : checkpoint.label}
                  </p>
                  <p>{checkpoint.note}</p>
                </div>
                <div className="seller-actions">
                  <button type="button" onClick={() => startEdit(item)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => deleteProduct(item._id)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            );
          })}
          {filteredSellerItems.length === 0 ? <p>No products found for this filter.</p> : null}
        </div>
      </section>
    </div>
  );
};

const sellerMetricGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
  gap: 12,
  margin: "14px 0",
};

const quickNav = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 14,
};

const sellerMetricCard = {
  display: "grid",
  gap: 6,
  textAlign: "left",
  border: "1px solid #dbeafe",
  borderRadius: 16,
  background: "#fff",
  padding: 14,
  color: "#0f172a",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
  cursor: "pointer",
};

export default SellerDashboard;
